<template>
  <loader v-if="loading" size="large" />

  <div v-else class="financial-overview__container">
    <h3 class="financial-overview__heading">
      {{ i18n.t('/FinancialOverview/Modal Title') }}
    </h3>
    <h5 v-if="configuration" class="financial-overview__title">
      {{ quote.Metadata.Name }} {{ configuration.CatalogCarName }}
    </h5>
    <dl class="financial-overview__details">
      <dt>
        {{ i18n.t('/FinancialOverview/Details/Quote Id') }}
      </dt>
      <dd>{{ quote.QuoteId }}</dd>
      <dt>
        {{
          isStockCar
            ? i18n.t('/FinancialOverview/Details/Stockcar Id')
            : i18n.t('/FinancialOverview/Details/Configuration Code')
        }}
      </dt>
      <dd>{{ isStockCar ? stockCarId : configuration?.ConfigurationCode }}</dd>
      <dt>
        {{ i18n.t('/FinancialOverview/Details/Created On') }}
      </dt>
      <dd>{{ formatDate(quote.Metadata.CreatedOn) }}</dd>
      <dt>
        {{ i18n.t('/FinancialOverview/Details/Last Updated On') }}
      </dt>
      <dd>{{ formatDate(quote.Metadata.LastUpdatedOn) }}</dd>
      <dt>
        {{ i18n.t('/FinancialOverview/Details/Last Updated By') }}
      </dt>
      <dd>{{ quote.Metadata.LastUpdatedBy }}</dd>
      <dt>
        {{ i18n.t('/FinancialOverview/Details/Establishment') }}
      </dt>
      <dd>{{ quote.QuoteDetails.Dealer.EstablishmentID }}</dd>
    </dl>

    <financial-overview-table
      :header-title="i18n.t('/FinancialOverview/Configuration Table Title')"
      :header-column1-title="i18n.t('/FinancialOverview/Configuration Table Excl Vat Title')"
      :header-column2-title="i18n.t('/FinancialOverview/Configuration Table Incl Vat Title')"
      :items="configurationItems"
      :footer-title="i18n.t('/FinancialOverview/Subtotal Configuration')"
      :footer-price="fullConfiguration"
    />

    <dl class="financial-overview__excl-summary">
      <dt>
        {{ i18n.t('/FinancialOverview/Subtotal Configuration Incl Bpm Title') }}
      </dt>
      <dd>{{ i18n.n(fullConfigurationWithBpm.Excl, 'currency') }}</dd>
    </dl>

    <financial-overview-table
      :header-title="i18n.t('/FinancialOverview/Control Codes Table Title')"
      :items="controlCodeItems"
      :footer-title="i18n.t('/FinancialOverview/Subtotal Configuration Incl Control Codes')"
      :footer-price="controlCodesPrice"
    />

    <financial-overview-table
      :header-title="i18n.t('/FinancialOverview/Delivery Costs Table Title')"
      :items="deliveryCosts"
      :footer-title="i18n.t('/FinancialOverview/Subtotal Configuration Incl Delivery Costs')"
      :footer-price="deliveryCostsPrice"
    />

    <financial-overview-table
      :header-title="i18n.t('/FinancialOverview/Dealer Accessories Table Title')"
      :items="dealerAccessories"
      :footer-title="i18n.t('/FinancialOverview/Subtotal Configuration Incl Dealer Accessories')"
      :footer-price="dealerAccessoriesPrice"
    />

    <financial-overview-table
      :header-title="i18n.t('/FinancialOverview/Corrections Table Title')"
      :items="corrections"
      :footer-title="i18n.t('/FinancialOverview/Subtotal Configuration Incl Corrections')"
      :footer-price="correctionsPrice"
    />

    <dl class="financial-overview__summary">
      <dt>
        {{ i18n.t('/FinancialOverview/Subtotal Corrections Excl Vat') }}
      </dt>
      <dd>{{ i18n.n(correctedTotalPrice.Excl, 'currency') }}</dd>

      <dt>{{ i18n.t('/FinancialOverview/Subtotal Corrections Vat') }}</dt>
      <dd>{{ i18n.n(correctedTotalPrice.Btw, 'currency') }}</dd>

      <dt>{{ i18n.t('/FinancialOverview/Subtotal Corrections Bpm') }}</dt>
      <dd>{{ i18n.n(correctedTotalPrice.BPM, 'currency') }}</dd>

      <dt>{{ i18n.t('/FinancialOverview/Subtotal Corrections Total') }}</dt>
      <dd>{{ i18n.n(correctedTotalPrice.Incl, 'currency') }}</dd>
    </dl>

    <financial-overview-exchange-car />

    <dl class="financial-overview__summary">
      <dt>
        {{ i18n.t('/FinancialOverview/Subtotal Company Investment Excl Vat') }}
      </dt>
      <dd>
        {{ i18n.n(quote.Metadata.Prices.AmountDue.ExclBTWinclBPM, 'currency') }}
      </dd>

      <dt>
        {{ i18n.t('/FinancialOverview/Subtotal Company Investment Vat') }}
      </dt>
      <dd>{{ i18n.n(quote.Metadata.Prices.AmountDue.Btw, 'currency') }}</dd>

      <dt>
        {{ i18n.t('/FinancialOverview/Subtotal Company Investment Total') }}
      </dt>
      <dd>{{ i18n.n(quote.Metadata.Prices.AmountDue.Incl, 'currency') }}</dd>
    </dl>

    <table class="financial-overview__table">
      <thead>
        <tr>
          <th class="financial-overview__wide-column">
            {{ i18n.t('/FinancialOverview/Subtotal Settle Costs Table Title') }}
          </th>
          <th>
            {{ i18n.t('/FinancialOverview/Subtotal Settle Costs Buy Title') }}
          </th>
          <th>
            {{ i18n.t('/FinancialOverview/Subtotal Settle Costs Sell Title') }}
          </th>
        </tr>
      </thead>

      <tbody>
        <tr
          v-for="nonRecoverableCost in nonRecoverableCosts"
          :key="nonRecoverableCost.OptionType_ID"
        >
          <td>{{ nonRecoverableCost.ID }} - {{ nonRecoverableCost.Name }}</td>
          <td>
            {{ i18n.n(nonRecoverableCost.Price.PurchaseExcl, 'currency') }}
          </td>
          <td>{{ i18n.n(nonRecoverableCost.Price.Excl, 'currency') }}</td>
        </tr>
        <tr v-for="bonus in bonuses" :key="bonus.OptionType_ID">
          <td>{{ bonus.ID }} - {{ bonus.Name }}</td>
          <td>
            {{ i18n.n(bonus.Price.PurchaseExcl, 'currency') }}
          </td>
          <td>{{ i18n.n(bonus.Price.Excl, 'currency') }}</td>
        </tr>
      </tbody>
    </table>
    <div class="button--actions">
      <app-button data-qa="vcr-print-button" @click="print">{{
        i18n.t('/Vcr/Signature/Print Label')
      }}</app-button>
    </div>
  </div>
</template>

<script>
import FinancialOverviewTable from '@/components/generic/presentational/organisms/financialOverview/FinancialOverviewTable.vue'
import FinancialOverviewExchangeCar from '@/components/generic/presentational/organisms/financialOverview/FinancialOverviewExchangeCar.vue'
import { mapGetters } from 'vuex'
import AppButton from '@/components/generic/presentational/atoms/appButton/AppButton.vue'
import DateHelper from '@/helpers/dateHelper'
import moment from 'moment'
import PdfApi from '@/api/server/Pdf.api'
import { saveAs } from 'file-saver'
import { EventBus } from '@/plugins/event-bus'
import Loader from '@/components/generic/presentational/atoms/loader/Loader.vue'
import pageToPrint from '@/helpers/enums/pageToPrint'

export default {
  components: {
    FinancialOverviewTable,
    FinancialOverviewExchangeCar,
    AppButton,
    Loader,
  },
  emits: ['loaded'],
  data() {
    return {
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,
      loading: false,
    }
  },
  computed: {
    ...mapGetters({
      quote: 'quote/get',
      isStockCar: 'quote/isStockCar',
      configuration: 'configuration/configuration',
      stockCarId: 'configuration/stockCarId',
      correctedTotalPrice: 'quote/totalPrice',
      accessoriesWithCategories: 'quote/accessoriesWithCategories',
    }),
    deliveryCosts() {
      return this.accessoriesWithCategories('DeliveryCosts')
    },
    dealerAccessories() {
      return this.accessoriesWithCategories('DealerAccessories')
    },
    nonRecoverableCosts() {
      return this.accessoriesWithCategories('NonRecoverableCosts')
    },
    bonuses() {
      return this.accessoriesWithCategories('Bonuses')
    },
    corrections() {
      return this.accessoriesWithCategories('Corrections')
    },
    configurationItems() {
      return [
        {
          Name: this.i18n.t('/FinancialOverview/Configuration Table Row Text'),
          Price: {
            Excl: this.configurationPrice.Excl,
            Incl: this.configurationPrice.Incl,
          },
        },
        ...this.configurationOptions,
      ].map((item) => {
        item.ID = item.ShortId
        return item
      })
    },
    controlCodeItems() {
      return [...this.controlCodes].map((item) => {
        item.ID = item.ShortId
        return item
      })
    },
    controlCodesPrice() {
      return {
        Incl:
          this.controlCodes.reduce((a, b) => Number(a) + Number(b?.Price?.Incl), 0) +
          this.fullConfiguration.Incl,
        Excl:
          this.controlCodes.reduce((a, b) => Number(a) + Number(b?.Price?.Excl), 0) +
          this.fullConfiguration.Excl,
      }
    },
    configurationPrice() {
      return {
        Incl: this.fullConfiguration.Incl - this.optionsPrice.Incl,
        Excl: this.fullConfiguration.Excl - this.optionsPrice.Excl,
      }
    },
    fullConfiguration() {
      return {
        Incl: Number(this.configuration?.Price?.Incl),
        Excl: Number(this.configuration?.Price?.Excl),
      }
    },
    fullConfigurationWithBpm() {
      return {
        Excl: Number(this.configuration?.Price?.ExclBTWinclBPM),
      }
    },
    optionsPrice() {
      return {
        Incl: Number(this.configuration?.OptionsTotalPrice?.Incl),
        Excl: Number(this.configuration?.OptionsTotalPrice?.Excl),
      }
    },
    correctionsPrice() {
      return {
        Incl:
          this.corrections.reduce((a, b) => Number(a) + Number(b?.Price?.Incl), 0) +
          this.dealerAccessoriesPrice.Incl,
        Excl:
          this.corrections.reduce((a, b) => Number(a) + Number(b?.Price?.Excl), 0) +
          this.dealerAccessoriesPrice.Excl,
      }
    },
    deliveryCostsPrice() {
      return {
        Incl:
          this.deliveryCosts.reduce((a, b) => Number(a) + Number(b?.Price?.Incl), 0) +
          this.controlCodesPrice.Incl,
        Excl:
          this.deliveryCosts.reduce((a, b) => Number(a) + Number(b?.Price?.Excl), 0) +
          this.controlCodesPrice.Excl,
      }
    },
    dealerAccessoriesPrice() {
      return {
        Incl:
          this.dealerAccessories.reduce((a, b) => Number(a) + Number(b?.Price?.Incl), 0) +
          this.deliveryCostsPrice.Incl,
        Excl:
          this.dealerAccessories.reduce((a, b) => Number(a) + Number(b?.Price?.Excl), 0) +
          this.deliveryCostsPrice.Excl,
      }
    },
    configurationOptions() {
      let configurationOptions = []

      const additionalConfigurationOptions = [
        {
          Label: this.i18n.t('/FinancialOverview/Configuration Table Color'),
          Source: this.configuration?.Color,
        },
        {
          Label: this.i18n.t('/FinancialOverview/Configuration Table Interior'),
          Source: this.configuration?.Interior,
        },
        {
          Label: this.i18n.t('/FinancialOverview/Configuration Table Rim'),
          Source: this.configuration?.Rim,
        },
      ]

      additionalConfigurationOptions.forEach((additionalConfigurationOption) => {
        if (additionalConfigurationOption.Source) {
          configurationOptions.push({
            ShortId: additionalConfigurationOption.Source?.ShortId,
            Name: `${additionalConfigurationOption.Label}: ${
              additionalConfigurationOption.Source?.Name || ''
            }`,
            Price: {
              Incl: additionalConfigurationOption.Source?.Price?.Incl,
              Excl: additionalConfigurationOption.Source?.Price?.Excl,
            },
          })
        }
      })

      this.configuration?.Categories?.forEach((category) => {
        category?.Features?.forEach((feature) => {
          configurationOptions.push({
            ShortId: feature.ShortId,
            Name: feature.Name,
            Price: {
              Incl: feature.Price?.Incl,
              Excl: feature.Price?.Excl,
            },
          })
        })
      })

      return configurationOptions
    },
    controlCodes() {
      return this.quote?.QuoteDetails?.ControlCodes || []
    },
  },
  mounted() {
    this.$emit('loaded')
  },
  methods: {
    getHtml() {
      if (typeof this.$el.getHTML !== 'function') {
        const errorMsg = 'Browser does not support getHTML API.'
        console.error(errorMsg)
        throw new Error(errorMsg)
      }

      return this.$el.getHTML()
    },
    print() {
      const route = this.$router.resolve({
        name: 'print-page',
        params: {
          ...this.$route.params,
          page: pageToPrint.financialOverview,
        },
      }).href
      window.open(route)
    },
    async printNew() {
      this.loading = true

      try {
        let html = this.getHtml()
        const data = await PdfApi.GenerateHtmlToPdf(
          html,
          'financieel-overzicht',
          'FinancialOverview'
        )

        saveAs(data.pdfContent, data.fileName)
      } catch (e) {
        EventBus.$emit('notification-warning', 'Niet gelukt om de Pdf te downloaden')
      } finally {
        this.loading = false
      }
    },
    formatDate(date) {
      return DateHelper.formatDate(moment.utc(date), true)
    },
  },
}
</script>

<style lang="scss" scoped>
.financial-overview {
  &__heading {
    display: none;
  }

  &__title {
    text-align: center;
  }

  &__table {
    width: 100%;
    margin-bottom: 1rem;

    tfoot {
      font-weight: bold;
    }

    td,
    th {
      padding: 0;
    }
  }

  &__wide-column {
    width: 80%;
  }

  &__summary,
  &__excl-summary,
  &__details {
    display: flex;
    flex-wrap: wrap;
    margin-bottom: 1rem;
    padding-left: 1rem;

    > dt {
      flex-grow: 1;
    }
  }

  &__excl-summary {
    font-weight: bold;

    > dt {
      width: auto;
    }

    > dd {
      width: 50%;
    }
  }

  &__summary {
    > dd {
      width: 10%;
    }

    > dt {
      width: 90%;
    }
  }

  &__details {
    padding-left: 0;

    > dt {
      flex-basis: 20%;
    }
    > dd {
      flex-basis: 70%;
      flex-grow: 1;
    }
  }
}
</style>
